/**
 * Messages tab — customer view of their own contact threads.
 *
 * Shows a list of the user's threads on the left, the selected thread
 * with the full conversation on the right, and an inline reply box.
 * Mirrors the AdminMessages layout but read-only for the admin-side
 * controls (close/reopen).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Inbox, Send, Mail } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-GB');
  } catch { return ''; }
};

const MessagesTab = () => {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadThreads = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contact/threads/mine`, { withCredentials: true });
      setThreads(r.data?.threads || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your messages.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (tid) => {
    setActiveId(tid);
    setReply('');
    setActiveThread(null);
    try {
      const r = await axios.get(`${API}/contact/threads/${tid}`, { withCredentials: true });
      setActiveThread(r.data);
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load thread.');
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/contact/threads/${activeId}/reply`,
        { body: reply.trim() },
        { withCredentials: true },
      );
      toast.success('Reply sent.');
      setReply('');
      openThread(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send your reply.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="messages-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-[#020617] mb-1">Messages</h2>
          <p className="text-sm text-[#64748B]">Your conversations with the Forge Ledger team.</p>
        </div>
        <Button
          asChild
          className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
          data-testid="messages-tab-new-thread-button"
        >
          <Link to="/contact">
            <Mail className="w-4 h-4 mr-1.5" /> New message
          </Link>
        </Button>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-4 min-h-[500px]">
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid="messages-tab-thread-list">
          {loading ? (
            <div className="p-5 text-sm text-[#64748B]">Loading…</div>
          ) : threads.length === 0 ? (
            <div className="p-5 text-center">
              <Inbox className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" />
              <p className="text-sm text-[#64748B]">No messages yet. Use the &quot;New message&quot; button to get in touch.</p>
            </div>
          ) : (
            <ul>
              {threads.map((t) => (
                <li key={t.thread_id}>
                  <button
                    type="button"
                    onClick={() => openThread(t.thread_id)}
                    className={`w-full text-left px-4 py-3 border-b border-[#E2E8F0] transition-colors ${activeId === t.thread_id ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'}`}
                    data-testid={`messages-tab-thread-row-${t.thread_id}`}
                  >
                    <div className="font-semibold text-[#020617] text-sm truncate mb-1">
                      {t.has_unread_for_user && (
                        <span className="inline-block w-2 h-2 bg-[#0052FF] rounded-full mr-1.5" data-testid={`messages-tab-unread-dot-${t.thread_id}`}></span>
                      )}
                      {t.subject}
                    </div>
                    <div className="text-xs text-[#64748B] mb-1">{fmtTime(t.last_message_at)} · {t.status}</div>
                    <div className="text-xs text-[#94A3B8] truncate">{t.last_message_preview}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl flex flex-col" data-testid="messages-tab-thread-detail">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#64748B] p-6 text-center">
              {threads.length === 0 ? 'Nothing here yet.' : 'Select a thread on the left.'}
            </div>
          ) : (
            <>
              <div className="p-5 border-b border-[#E2E8F0]">
                <h3 className="text-lg font-extrabold text-[#020617]" data-testid="messages-tab-active-subject">{activeThread.subject}</h3>
                <div className="text-xs text-[#64748B] mt-1 capitalize">Status: {activeThread.status}</div>
              </div>

              <div className="flex-1 p-5 space-y-4 overflow-y-auto max-h-[55vh]" data-testid="messages-tab-message-list">
                {activeThread.messages.map((m) => {
                  const isAdmin = m.sender_type === 'admin';
                  return (
                    <div key={m.message_id} className={`max-w-[80%] ${isAdmin ? '' : 'ml-auto'}`} data-testid={`messages-tab-message-${m.message_id}`}>
                      <div className={`text-[11px] font-semibold uppercase tracking-wider mb-1 ${isAdmin ? 'text-[#0052FF]' : 'text-[#64748B] text-right'}`}>
                        {isAdmin ? 'Forge Ledger Team' : 'You'} · {fmtTime(m.created_at)}
                      </div>
                      <div className={`p-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${isAdmin ? 'bg-[#F1F5F9] text-[#020617]' : 'bg-[#0052FF] text-white'}`}>
                        {m.body}
                      </div>
                    </div>
                  );
                })}
              </div>

              {activeThread.status === 'open' && (
                <div className="p-4 border-t border-[#E2E8F0]">
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type your reply…"
                    rows={3}
                    maxLength={5000}
                    className="mb-3"
                    data-testid="messages-tab-reply-input"
                  />
                  <Button
                    type="button"
                    onClick={sendReply}
                    disabled={submitting || !reply.trim()}
                    className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
                    data-testid="messages-tab-reply-submit"
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    {submitting ? 'Sending…' : 'Reply'}
                  </Button>
                </div>
              )}
              {activeThread.status === 'closed' && (
                <div className="p-4 border-t border-[#E2E8F0] text-sm text-[#64748B]">
                  This thread is closed. <Link to="/contact" className="text-[#0052FF] hover:underline">Start a new one</Link> if you need more help.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesTab;
